import type { EngineService } from "../platform";
import type { FileTreeNode } from "../store/editor/types";
import { getDocumentBundleDir } from "./publish-utils";

export interface PersistedOutputWatchDefinition {
    id: string;
    unitId: string;
    path: string;
    label: string;
    includePartial: boolean;
    enabled: boolean;
    createdAt?: string;
    updatedAt?: string;
}

export interface PersistedOutputWatchIndex {
    version: 1;
    kind: "magic-md.output-watches";
    documentPath: string;
    updatedAt: string;
    watches: PersistedOutputWatchDefinition[];
}

export function getOutputWatchDirPath(documentPath: string): string {
    return `${getDocumentBundleDir(documentPath)}/watches`;
}

export function getOutputWatchIndexPath(documentPath: string): string {
    return `${getOutputWatchDirPath(documentPath)}/index.json`;
}

export function isDocumentWatchesPath(path: string): boolean {
    return /\.magic-md\/watches$/.test(path);
}

export function isDocumentWatchItemPath(path: string): boolean {
    return /\.magic-md\/watches\/[^/]+\.watch$/.test(path);
}

export function getOutputWatchItemIdFromPath(path: string): string | null {
    const match = /\.magic-md\/watches\/([^/]+)\.watch$/.exec(path);
    if (!match) return null;
    try {
        return decodeURIComponent(match[1]);
    } catch {
        return match[1];
    }
}

export function getDocumentPathFromOutputWatchPath(path: string): string | null {
    const match = /^(.*\.magic-md)\/watches(?:\/|$)/.exec(path);
    if (!match) return null;
    return `${match[1].replace(/\.magic-md$/i, "")}.md`;
}

export function getOutputWatchItemPath(documentPath: string, watchId: string): string {
    return `${getOutputWatchDirPath(documentPath)}/${encodeURIComponent(watchId)}.watch`;
}

export async function buildOutputWatchVirtualChildren(
    engine: EngineService,
    watchesPath: string,
): Promise<FileTreeNode[]> {
    const documentPath = getDocumentPathFromOutputWatchPath(watchesPath);
    if (!documentPath) return [];
    const index = await readOutputWatchIndex(engine, documentPath);
    return index.watches.map((watch) => ({
        name: watch.label,
        path: getOutputWatchItemPath(documentPath, watch.id),
        isDirectory: false,
        isVirtual: true as const,
        loaded: true,
        expanded: false,
    }));
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return !!value && typeof value === "object" && !Array.isArray(value);
}

function normalizeWatchDefinition(
    value: unknown,
    index: number,
): PersistedOutputWatchDefinition | null {
    if (!isRecord(value)) return null;
    const unitId = typeof value.unitId === "string" ? value.unitId.trim() : "";
    const path = typeof value.path === "string" ? value.path.trim() : "";
    if (!unitId || !path) return null;
    const id = typeof value.id === "string" && value.id.trim()
        ? value.id.trim()
        : `watch-${index + 1}`;
    const label = typeof value.label === "string" && value.label.trim()
        ? value.label.trim()
        : `${unitId}.${path}`;
    return {
        id,
        unitId,
        path,
        label,
        includePartial: value.includePartial === true,
        enabled: value.enabled !== false,
        createdAt: typeof value.createdAt === "string" ? value.createdAt : undefined,
        updatedAt: typeof value.updatedAt === "string" ? value.updatedAt : undefined,
    };
}

export function parseOutputWatchIndex(
    content: string,
    documentPath: string,
): PersistedOutputWatchIndex {
    const parsed = JSON.parse(content) as unknown;
    const watches = isRecord(parsed) && Array.isArray(parsed.watches)
        ? parsed.watches
            .map((item, index) => normalizeWatchDefinition(item, index))
            .filter((item): item is PersistedOutputWatchDefinition => !!item)
        : [];
    const updatedAt = isRecord(parsed) && typeof parsed.updatedAt === "string"
        ? parsed.updatedAt
        : new Date(0).toISOString();
    return {
        version: 1,
        kind: "magic-md.output-watches",
        documentPath,
        updatedAt,
        watches,
    };
}

export async function readOutputWatchIndex(
    engine: EngineService,
    documentPath: string,
): Promise<PersistedOutputWatchIndex> {
    try {
        const { content } = await engine.workspaceReadFile({
            path: getOutputWatchIndexPath(documentPath),
        });
        return parseOutputWatchIndex(content, documentPath);
    } catch {
        return {
            version: 1,
            kind: "magic-md.output-watches",
            documentPath,
            updatedAt: new Date(0).toISOString(),
            watches: [],
        };
    }
}

export async function writeOutputWatchIndex(
    engine: EngineService,
    documentPath: string,
    watches: PersistedOutputWatchDefinition[],
): Promise<void> {
    const updatedAt = new Date().toISOString();
    const index: PersistedOutputWatchIndex = {
        version: 1,
        kind: "magic-md.output-watches",
        documentPath,
        updatedAt,
        watches: watches.map((watch) => ({
            ...watch,
            updatedAt: watch.updatedAt ?? updatedAt,
        })),
    };
    await engine.workspaceWriteFile({
        path: getOutputWatchIndexPath(documentPath),
        content: `${JSON.stringify(index, null, 2)}\n`,
        createParents: true,
    });
}
